import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import LegalFooter from "@/components/legal/LegalFooter";

/**
 * LegalLayout
 * Shared chrome for static legal pages (Terms / Privacy / Refund).
 * Provides: top nav, sticky table-of-contents sidebar on desktop,
 *           prose container with consistent typography, footer.
 *
 * Pass `sections` as [{ id, label }, ...] to render the TOC.
 * Children render inside a prose-styled wrapper — use <section id={id}>.
 */
export default function LegalLayout({ title, subtitle, lastUpdated, sections = [], children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeId, setActiveId] = useState(sections[0]?.id || "");

  // Highlight the active TOC item based on scroll
  useEffect(() => {
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0.1 },
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Top nav (matches landing page chrome) */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-semibold tracking-tight">
              AgentFlow<span className="text-blue-600">.</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm">
            <Link to="/pricing" className="text-slate-600 hover:text-slate-900 transition">Pricing</Link>
            <Link to="/terms" className="text-slate-600 hover:text-slate-900 transition">Terms</Link>
            <Link to="/privacy" className="text-slate-600 hover:text-slate-900 transition">Privacy</Link>
            <Link to="/refund" className="text-slate-600 hover:text-slate-900 transition">Refunds</Link>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-slate-700 hover:text-slate-900 transition">Sign in</Link>
            <Link to="/register">
              <Button className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white">Start Free</Button>
            </Link>
          </div>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 -mr-2" aria-label="Toggle menu">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <div className="px-6 py-4 flex flex-col gap-3 text-sm">
              <Link to="/pricing" onClick={() => setMobileOpen(false)} className="text-slate-700">Pricing</Link>
              <Link to="/terms" onClick={() => setMobileOpen(false)} className="text-slate-700">Terms</Link>
              <Link to="/privacy" onClick={() => setMobileOpen(false)} className="text-slate-700">Privacy</Link>
              <Link to="/refund" onClick={() => setMobileOpen(false)} className="text-slate-700">Refunds</Link>
              <Link to="/login" onClick={() => setMobileOpen(false)} className="text-slate-700">Sign in</Link>
              <Link to="/register" onClick={() => setMobileOpen(false)}>
                <Button className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white">Start Free</Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white border-b border-slate-200/70">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-14 lg:py-20">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">Legal</p>
          <h1 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-4 text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl">{subtitle}</p>
          )}
          {lastUpdated && (
            <p className="mt-6 text-xs text-slate-500 font-mono">Last updated: {lastUpdated}</p>
          )}
        </div>
      </section>

      {/* Body */}
      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10 lg:gap-16">
        {/* TOC sidebar */}
        {sections.length > 0 && (
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">On this page</p>
              <ul className="space-y-1.5 text-sm">
                {sections.map(({ id, label }) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      data-testid={`toc-${id}`}
                      className={
                        "block py-1 px-2 rounded-md transition border-l-2 " +
                        (activeId === id
                          ? "border-blue-600 text-blue-700 bg-blue-50 font-medium"
                          : "border-transparent text-slate-600 hover:text-slate-900")
                      }
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}

        {/* Prose container */}
        <article className="prose prose-slate max-w-none
                            prose-headings:font-display prose-headings:tracking-tight
                            prose-h2:text-2xl prose-h2:font-semibold prose-h2:mt-12 prose-h2:mb-4
                            prose-h3:text-lg prose-h3:font-semibold prose-h3:mt-8 prose-h3:mb-3
                            prose-p:text-slate-700 prose-p:leading-relaxed prose-p:text-[15px]
                            prose-li:text-slate-700 prose-li:text-[15px]
                            prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                            prose-strong:text-slate-900">
          {children}
        </article>
      </main>

      <LegalFooter />
    </div>
  );
}
